import React, { useState, useEffect } from 'react';
import { Users, Calendar, MapPin, Mail, Phone, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

const ArtistBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, completed

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/artist/bookings');
      setBookings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const action = newStatus === 'confirmed' ? 'accept' : newStatus === 'cancelled' ? 'decline' : 'complete';
      await api.put(`/artist/bookings/${bookingId}/${action}`);
      toast.success(`Booking ${newStatus}`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'pending': return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-blue-400" />;
      case 'cancelled': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'completed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const filteredBookings = bookings.filter(b => {
    if (filter === 'all') return true;
    return b.booking_status === filter;
  });

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-400" />
            <h1 className="text-3xl font-bold text-white">My Bookings</h1>
          </div>
          <div className="text-gray-400">
            {bookings.length} total booking{bookings.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{bookings.filter(b => b.booking_status === 'pending').length}</p>
            <p className="text-sm text-gray-400">Pending</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{bookings.filter(b => b.booking_status === 'confirmed').length}</p>
            <p className="text-sm text-gray-400">Confirmed</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{bookings.filter(b => b.booking_status === 'completed').length}</p>
            <p className="text-sm text-gray-400">Completed</p>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <p className="text-2xl font-bold text-white">{bookings.filter(b => b.booking_status === 'cancelled').length}</p>
            <p className="text-sm text-gray-400">Cancelled</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className="capitalize"
            >
              {f}
            </Button>
          ))}
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-xl">
            <Calendar className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No bookings found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map(booking => (
              <div
                key={booking.booking_id}
                className="bg-white/5 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* Customer Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 flex items-center justify-center text-white font-bold">
                        {booking.customer?.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{booking.customer?.name || 'Unknown Customer'}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Mail className="w-3 h-3" />
                          {booking.customer?.email || 'No email'}
                        </div>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar className="w-4 h-4 text-purple-400" />
                        <span>{booking.event_date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Clock className="w-4 h-4 text-purple-400" />
                        <span>{booking.event_duration || 4} hours</span>
                      </div>
                      {booking.event_location?.address && (
                        <div className="flex items-center gap-2 text-gray-300 sm:col-span-2">
                          <MapPin className="w-4 h-4 text-purple-400" />
                          <span className="truncate">{booking.event_location.address}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(booking.booking_status)}
                      <span className={`text-xs px-3 py-1 rounded-full border ${getStatusColor(booking.booking_status)}`}>
                        {booking.booking_status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {booking.booking_status === 'pending' && (
                  <div className="mt-4 pt-4 border-t border-gray-700 flex gap-3">
                    <Button
                      size="sm"
                      onClick={() => updateBookingStatus(booking.booking_id, 'confirmed')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateBookingStatus(booking.booking_id, 'cancelled')}
                      className="border-red-500 text-red-400 hover:bg-red-500/20"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Decline
                    </Button>
                  </div>
                )}

                {booking.booking_status === 'confirmed' && (
                  <div className="mt-4 pt-4 border-t border-gray-700 flex gap-3 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => updateBookingStatus(booking.booking_id, 'completed')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Completed
                    </Button>
                  </div>
                )}

                {/* Booking ID */}
                <p className="text-xs text-gray-500 mt-4">Booking ID: {booking.booking_id}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistBookings;